// ==========================================================================
// booking module
// ==========================================================================
/* jshint esnext: true */
import {APP_NAME, $document} from '../utils/environment';
import AbstractModule from './AbstractModule';

const MODULE_NAME = 'Booking';
const EVENT_NAMESPACE = `${APP_NAME}.${MODULE_NAME}`;

const EVENT = {
    CHANGE: `change.${EVENT_NAMESPACE}`,
    SUBMIT: `submit.${EVENT_NAMESPACE}`
};

const EXTERNAL = 'external';
const INTERNAL = 'internal';

export default class extends AbstractModule {
    constructor(options) {
        super(options);

        this.isTransmitting = false;
        this.xhr = null;
        this.selectedDeparture = null;
        this.state = null;

        this.controls = this.$el.find('.js-booking-controls');
        this.tourSelect = this.$el.find('.js-tour-select');
        this.numSeats = this.$el.find('.js-num-seats');
        this.dateInput = this.$el.find('.js-input-date');
        this.departureInput = this.$el.find('.js-input-departure');
        this.$checkout = this.$el.find('.js-checkout');
        this.$bookingControls = this.$el.find('.js-booking-control');
        this.$externalBookingControls = this.$el.find('.js-external-booking-control');
        this.$externalDescription = this.$el.find('.js-external-description');
        this.$externalLink = this.$el.find('.js-external-link');
    }

    // ==========================================================================
    // Initialisation
    // ==========================================================================

    init() {

        // Init events listeners
        this.events();

        // First update of the calendar using the default data provided by the form.
        this.updateWidget();
    }

    updateWidget() {
        if (this.tourSelect.find(':selected').data('external') !== 1) {
            this.toggleWidgetState(INTERNAL);
            this.updateCalendar();
            return;
        }

        let description = this.tourSelect.find(':selected').data('external-description');
        let link = this.tourSelect.find(':selected').data('external-link');

        this.$externalDescription.fadeOut(function() {
            $(this).html(description).fadeIn();
        });
        this.$externalLink.attr('href', link);

        this.toggleWidgetState(EXTERNAL);
    }

    updateCalendar() {

        if (this.xhr && this.isTransmitting) {
            this.xhr.abort();
            this.toggleCalendarLoading();
        }

        // Tour id
        let tour = this.tourSelect.val();

        // Number of seats needed
        let seatsNumber = this.numSeats.val();

        let data = {
            num_seats: seatsNumber
        };

        this.toggleCalendarLoading();

        this.xhr = $.get(
            'api/v1/tour/' + tour + '/departures',
            data,
            (response) => {
                let events = response.data.departures;

                $document.triggerHandler({
                    type:   `setEvents.Englishbus.Calendar`,
                    events: events
                });

                // keep selected date if able to.
                if (this.selectedDeparture) {
                    let datetime = this.selectedDeparture.datetime;
                    for (let event in events) {
                        if (events[event].datetime === datetime && events[event].availability !== 'booked') {
                            // Communicate the selectedDate with calendar
                            $document.triggerHandler({
                                type:     `setSelectedDate.Englishbus.Calendar`,
                                datetime: datetime
                            });

                            this.toggleCalendarLoading();
                            return;
                        }
                    }
                }

                // Select the first available departure.
                for (let event in events) {
                    if (events[event].availability !== 'booked') {
                        this.setDefaultDeparture(events[event]);
                        this.toggleCalendarLoading();
                        return;
                    }
                }

                this.setDefaultDeparture();
                this.toggleCalendarLoading();
            });
    }

    setDefaultDeparture(departure) {
        this.selectedDeparture = null;
        if (departure !== undefined && departure.availability !== 'booked') {
            this.setCheckout(true);

            this.departureInput.val(departure.id).trigger('change');
            this.dateInput.val(departure.date.long);

            // Communicate the selectedDate with calendar
            $document.triggerHandler({
                type:     `setSelectedDate.Englishbus.Calendar`,
                datetime: departure.datetime
            })
        } else {
            this.setCheckout(false);
            this.departureInput.val(null).trigger('change');
            this.dateInput.val('No departures for the moment.');
        }
    }

    setSelectedDeparture(departure) {
        if (departure !== undefined) {
            this.setCheckout(true);

            this.departureInput.val(departure.id).trigger('change');
            this.dateInput.val(departure.date.long);

            this.selectedDeparture = departure;
        }
    }

    setCheckout(state) {
        this.$checkout.attr('disabled', !state);
    }

    /**
     * Simple method for toggling animation states
     *
     * @return void
     */
    toggleCalendarLoading() {
        this.isTransmitting = !this.isTransmitting;

        if (this.isTransmitting) {
            this.$el.addClass('is-loading');
        } else {
            setTimeout(() => {
                this.$el.removeClass('is-loading');
            }, 500);
        }
    }

    /**
     * Simple method for toggling animation states
     *
     * @return void
     */
    toggleWidgetState(state) {
        if (this.state === state) {
            return;
        }

        switch (state) {
            case EXTERNAL :
                this.$bookingControls.slideUp(
                    () => this.$externalBookingControls.slideDown()
                );
                break;
            case INTERNAL :
                this.$externalBookingControls.slideUp(
                    () => this.$bookingControls.slideDown()
                );
                break;
        }

        this.state = state;
    }

    // ==========================================================================
    // Event Listeners
    // ==========================================================================

    events() {
        this.controls.on(EVENT.CHANGE, () => this.updateWidget());

        $document.on(`selectEvent.Englishbus.Calendar`, (options) => this.setSelectedDeparture(options.departure))
    }

    destroy() {
        super.destroy();
        this.$el.off(`.${EVENT_NAMESPACE}`);
    }
}
